import {
  type HTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
  useId,
  useState,
} from 'react';

import { cn } from '@shared/lib/cn';
import { Spinner } from '@shared/ui/spinner';

import {
  LISTBOX_BOX_CLASS,
  LISTBOX_MAX_HEIGHT_CLASS,
  type ListboxOptionItem,
  type ListboxSelection,
  type ListboxStatus,
} from './listbox.constant';
import { edgeEnabledValue, findEnabledValue, listboxOptionId, toggleValue } from './listbox.lib';
import { ListboxSkeleton } from './listbox.skeleton.ui';
import { LISTBOX_TEXT } from './listbox.text';
import { ListboxOption } from './listbox-option.ui';

export interface ListboxProps extends Omit<
  HTMLAttributes<HTMLUListElement>,
  'children' | 'onChange' | 'defaultValue'
> {
  /** 선택지 */
  options: readonly ListboxOptionItem[];
  /** Figma `selection`. 기본 single */
  selection?: ListboxSelection;
  /** 선택된 값들(제어). single 은 0~1개 */
  value?: readonly string[];
  /** 비제어 초기값 */
  defaultValue?: readonly string[];
  /** 줄을 고르면(누르기 · Enter · Space) 다음 선택 값들과 고른 줄을 넘긴다 */
  onChange?: (value: string[], option: ListboxOptionItem) => void;
  /** 키보드로 가리킨 줄(제어). Select 처럼 포커스가 목록 밖에 있을 때 넘긴다 */
  activeValue?: string | null;
  onActiveValueChange?: (value: string | null) => void;
  /**
   * Figma `status`. init = 스켈레톤, empty = "항목이 없습니다", loading = 흐린 목록 + Spinner.
   * 선택지가 비어 있으면 default 여도 empty 모양을 그린다
   */
  status?: ListboxStatus;
  /** 빈 목록 안내(기본 "항목이 없습니다") */
  emptyText?: ReactNode;
  /**
   * 목록이 스스로 포커스를 받고 키보드(위 · 아래 · Home · End · Enter · Space)를 처리한다. 기본 true.
   * Select 처럼 포커스를 다른 요소(콤보박스)가 가지면 false
   */
  focusable?: boolean;
  ref?: Ref<HTMLUListElement>;
}

/**
 * 고를 수 있는 항목들의 목록 상자(`role="listbox"`). Select · MultiSelect 가 불러와 쓴다.
 * 너비는 부모를 채운다(Figma 240). 줄이 많으면 6줄 높이에서 스크롤한다.
 */
export function Listbox({
  options,
  selection = 'single',
  value: valueProp,
  defaultValue,
  onChange,
  activeValue: activeProp,
  onActiveValueChange,
  status = 'default',
  emptyText = LISTBOX_TEXT.empty,
  focusable = true,
  id: idProp,
  className,
  style,
  onKeyDown,
  ref,
  ...rest
}: ListboxProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const [valueState, setValueState] = useState<readonly string[]>(defaultValue ?? []);
  const value = valueProp ?? valueState;
  const [activeState, setActiveState] = useState<string | null>(null);
  const active = activeProp !== undefined ? activeProp : activeState;

  if (status === 'init') return <ListboxSkeleton className={className} />;

  const setActive = (next: string | null) => {
    if (activeProp === undefined) setActiveState(next);
    onActiveValueChange?.(next);
  };

  const choose = (option: ListboxOptionItem) => {
    if (option.disabled) return;
    const next = toggleValue(value, option.value, selection);
    if (valueProp === undefined) setValueState(next);
    setActive(option.value);
    onChange?.(next, option);
  };

  const loading = status === 'loading';
  const empty = status === 'empty' || (options.length === 0 && !loading);
  const activeIndex = options.findIndex((option) => option.value === active);

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || !focusable || empty || loading) return;
    switch (event.key) {
      case 'ArrowDown':
        setActive(findEnabledValue(options, active, 1));
        break;
      case 'ArrowUp':
        setActive(findEnabledValue(options, active, -1));
        break;
      case 'Home':
        setActive(edgeEnabledValue(options, 'first'));
        break;
      case 'End':
        setActive(edgeEnabledValue(options, 'last'));
        break;
      case 'Enter':
      case ' ': {
        const option = options[activeIndex];
        if (option) choose(option);
        break;
      }
      default:
        return;
    }
    event.preventDefault();
  };

  return (
    <ul
      ref={ref}
      id={id}
      role="listbox"
      aria-multiselectable={selection === 'multiple' || undefined}
      aria-busy={loading || undefined}
      aria-activedescendant={
        focusable && activeIndex >= 0 ? listboxOptionId(id, activeIndex) : undefined
      }
      tabIndex={focusable ? 0 : undefined}
      data-slot="listbox"
      data-selection={selection}
      data-status={empty ? 'empty' : status}
      {...rest}
      onKeyDown={handleKeyDown}
      style={style}
      className={cn(LISTBOX_BOX_CLASS, LISTBOX_MAX_HEIGHT_CLASS, 'outline-none', className)}
    >
      {empty ? (
        // 안내 줄: 사방 12 + 글자 20 = 44
        <li role="presentation" className="flex justify-center p-3 text-fg-muted typo-body-sm">
          {emptyText}
        </li>
      ) : (
        options.map((option, index) => (
          <ListboxOption
            key={option.value}
            id={listboxOptionId(id, index)}
            selection={selection}
            selected={value.includes(option.value)}
            disabled={option.disabled}
            active={option.value === active}
            onSelect={() => choose(option)}
            className={cn(loading && 'opacity-(--opacity-disabled)')}
          >
            {option.label}
          </ListboxOption>
        ))
      )}
      {loading ? (
        <li
          role="presentation"
          className={cn(
            'flex items-center justify-center',
            options.length > 0 ? 'absolute inset-0' : 'p-2-5',
          )}
        >
          <Spinner size="md" label={LISTBOX_TEXT.loading} />
        </li>
      ) : null}
    </ul>
  );
}
